import { Link } from 'react-router-dom';
import { PriceGroupSelect } from '@/features/settings';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  applyDiscount,
  marginPct,
  moneySchema,
  percentSchema,
  productPriceTiersWriteSchema,
  type ProductDetail,
  type ProductPriceTierWriteInput,
} from '@erp/shared';
import { Calculator, Loader2, Plus, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useUpdateProduct } from '@/features/products/api/use-products';
import { normalizeDecimalInput, textOrNull } from '@/features/products/lib/form-value';
import { formatMoney, formatPercent } from '@/lib/formatters';

const formSchema = z.object({ priceTiers: productPriceTiersWriteSchema });

type FormValues = { priceTiers: ProductPriceTierWriteInput[] };

function toFormValues(product: ProductDetail): FormValues {
  return {
    priceTiers: product.priceTiers.map((tier) => ({
      id: tier.id,
      priceGroupId: tier.priceGroupId,
      priceGroup: tier.priceGroup,
      currency: tier.currency,
      unitCode: product.baseUnit,
      minQuantity: tier.minQuantity,
      unitPrice: tier.unitPrice,
      discountPct: tier.discountPct,
      validFrom: tier.validFrom,
      validTo: tier.validTo,
    })),
  };
}

export interface PriceTierEditorProps {
  product: ProductDetail;
  canWrite: boolean;
}

export function PriceTierEditor({ product, canWrite }: PriceTierEditorProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const updateProduct = useUpdateProduct(product.id);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: toFormValues(product),
  });

  const fieldArray = useFieldArray({ control: form.control, name: 'priceTiers', keyName: 'key' });

  useEffect(() => {
    form.reset(toFormValues(product));
  }, [product, form]);

  const tiers = form.watch('priceTiers');
  const purchasePrice = moneySchema.safeParse(product.purchasePrice);
  const listPrice = moneySchema.safeParse(product.listPrice);

  const onSubmit = form.handleSubmit((values) => {
    updateProduct.mutate(
      { priceTiers: values.priceTiers },
      {
        onSuccess: () => toast.success(t('products.priceTiers.saved')),
        onError: (error) =>
          toast.error(t('products.priceTiers.saveError'), {
            description: t(error.message, { defaultValue: error.message }),
          }),
      },
    );
  });

  const calculateFromDiscount = (index: number): void => {
    const tier = tiers[index];
    if (tier === undefined || tier.currency !== product.currency || !listPrice.success) {
      return;
    }
    const discount = percentSchema.safeParse(tier.discountPct ?? '0');
    if (!discount.success) {
      return;
    }
    form.setValue(`priceTiers.${index}.unitPrice`, applyDiscount(listPrice.data, discount.data), {
      shouldDirty: true,
    });
    form.setValue(`priceTiers.${index}.discountPct`, '0', { shouldDirty: true });
  };

  const errors = form.formState.errors;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      {product.hasCalculatedPrices ? (
        <p className="text-sm text-muted-foreground">
          {t('pricing.managedPricesHint')}{' '}
          <Link className="underline" to="/products/pricing?tab=versions">
            {t('pricing.versions')}
          </Link>
        </p>
      ) : null}
      <p className="text-sm text-muted-foreground">
        {t('priceGroups.priceBasis', { unit: product.baseUnit })}
      </p>
      {product.priceTiers.some((tier) => tier.unitCode !== product.baseUnit) ? (
        <p role="alert" className="text-sm text-muted-foreground">
          {t('priceGroups.legacyBasis')}
        </p>
      ) : null}
      {Object.keys(errors).length > 0 ? (
        <p role="alert" className="text-sm text-destructive">
          {t('priceGroups.invalidPrices')}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground">{t('products.fields.listPrice')}</span>
          <span className="text-sm font-medium tabular-nums">
            {formatMoney(product.listPrice, product.currency, i18n.language)}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground">
            {t('products.fields.purchasePrice')}
          </span>
          <span className="text-sm font-medium tabular-nums">
            {formatMoney(product.purchasePrice, product.currency, i18n.language)}
          </span>
        </div>
        <p className="max-w-md text-xs text-muted-foreground">
          {t('products.priceTiers.calculatorHint')}
        </p>
      </div>

      {fieldArray.fields.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-md border border-border p-10 text-center">
          <p className="text-sm text-muted-foreground">{t('products.priceTiers.empty')}</p>
          {canWrite ? (
            <Button
              type="button"
              size="sm"
              onClick={() =>
                fieldArray.append({
                  priceGroupId: null,
                  priceGroup: null,
                  currency: product.currency,
                  unitCode: product.baseUnit,
                  minQuantity: '0',
                  unitPrice: product.listPrice ?? '',
                  discountPct: '0',
                  validFrom: null,
                  validTo: null,
                })
              }
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('products.priceTiers.add')}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('products.priceTiers.columns.priceGroup')}</TableHead>
                <TableHead>{t('products.priceTiers.columns.minQuantity')}</TableHead>
                <TableHead>{t('products.priceTiers.columns.discountPct')}</TableHead>
                <TableHead>{t('products.priceTiers.columns.unitPrice')}</TableHead>
                <TableHead>{t('priceGroups.currency')}</TableHead>
                <TableHead>{t('products.priceTiers.columns.margin')}</TableHead>
                <TableHead>{t('products.priceTiers.columns.validFrom')}</TableHead>
                <TableHead>{t('products.priceTiers.columns.validTo')}</TableHead>
                <TableHead>
                  <span className="sr-only">{t('common.delete')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fieldArray.fields.map((field, index) => {
                const tier = tiers[index];
                const unitPrice = moneySchema.safeParse(tier?.unitPrice);
                const discount = percentSchema.safeParse(tier?.discountPct ?? '0');
                const margin =
                  unitPrice.success && purchasePrice.success
                    ? marginPct(unitPrice.data, purchasePrice.data)
                    : null;
                const rowError = errors.priceTiers?.[index];

                return (
                  <TableRow key={field.key}>
                    <TableCell>
                      <Label htmlFor={`tier-group-${index}`} className="sr-only">
                        {t('products.priceTiers.columns.priceGroup')}
                      </Label>
                      <PriceGroupSelect
                        id={`tier-group-${index}`}
                        value={tier?.priceGroupId}
                        legacyLabel={tier?.priceGroup}
                        currency={tier?.currency ?? product.currency}
                        disabled={!canWrite}
                        onChange={(value) => {
                          form.setValue(`priceTiers.${index}.priceGroupId`, value, {
                            shouldDirty: true,
                          });
                          form.setValue(`priceTiers.${index}.priceGroup`, null);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Label htmlFor={`tier-qty-${index}`} className="sr-only">
                        {t('products.priceTiers.columns.minQuantity')}
                      </Label>
                      <Input
                        id={`tier-qty-${index}`}
                        inputMode="decimal"
                        disabled={!canWrite}
                        className="min-w-24 text-right tabular-nums"
                        aria-invalid={rowError?.minQuantity !== undefined}
                        aria-describedby={
                          rowError?.minQuantity === undefined
                            ? undefined
                            : `tier-qty-error-${index}`
                        }
                        {...form.register(`priceTiers.${index}.minQuantity`, {
                          setValueAs: normalizeDecimalInput,
                        })}
                      />
                      {rowError?.minQuantity === undefined ? null : (
                        <p
                          id={`tier-qty-error-${index}`}
                          role="alert"
                          className="mt-1 text-xs text-destructive"
                        >
                          {t('priceGroups.invalidMinQuantity')}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Label htmlFor={`tier-discount-${index}`} className="sr-only">
                          {t('products.priceTiers.columns.discountPct')}
                        </Label>
                        <Input
                          id={`tier-discount-${index}`}
                          inputMode="decimal"
                          disabled={!canWrite}
                          className="min-w-20 text-right tabular-nums"
                          aria-invalid={rowError?.discountPct !== undefined}
                          aria-describedby={
                            rowError?.discountPct === undefined
                              ? undefined
                              : `tier-discount-error-${index}`
                          }
                          {...form.register(`priceTiers.${index}.discountPct`, {
                            setValueAs: normalizeDecimalInput,
                          })}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={
                            !canWrite ||
                            tier?.currency !== product.currency ||
                            !discount.success ||
                            !listPrice.success
                          }
                          aria-label={t('products.priceTiers.calculate')}
                          title={t('products.priceTiers.calculate')}
                          onClick={() => calculateFromDiscount(index)}
                        >
                          <Calculator className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </div>
                      {rowError?.discountPct === undefined ? null : (
                        <p
                          id={`tier-discount-error-${index}`}
                          role="alert"
                          className="mt-1 text-xs text-destructive"
                        >
                          {t('priceGroups.invalidDiscountPct')}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Label htmlFor={`tier-price-${index}`} className="sr-only">
                        {t('products.priceTiers.columns.unitPrice')}
                      </Label>
                      <Input
                        id={`tier-price-${index}`}
                        inputMode="decimal"
                        disabled={!canWrite}
                        className="min-w-24 text-right tabular-nums"
                        aria-invalid={rowError?.unitPrice !== undefined}
                        aria-describedby={
                          rowError?.unitPrice === undefined
                            ? undefined
                            : `tier-price-error-${index}`
                        }
                        {...form.register(`priceTiers.${index}.unitPrice`, {
                          setValueAs: normalizeDecimalInput,
                        })}
                      />
                      {rowError?.unitPrice === undefined ? null : (
                        <p
                          id={`tier-price-error-${index}`}
                          role="alert"
                          className="mt-1 text-xs text-destructive"
                        >
                          {t('priceGroups.invalidUnitPrice')}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Label htmlFor={`tier-currency-${index}`} className="sr-only">
                        {t('priceGroups.currency')}
                      </Label>
                      <Input
                        id={`tier-currency-${index}`}
                        disabled={!canWrite}
                        className="w-20"
                        maxLength={3}
                        {...form.register(`priceTiers.${index}.currency`, {
                          setValueAs: (value: string) => value.toUpperCase(),
                        })}
                      />
                      <span className="text-xs text-muted-foreground">
                        {t('priceGroups.priceBasis', { unit: product.baseUnit })}
                      </span>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {margin === null ? '—' : formatPercent(margin, i18n.language)}
                    </TableCell>
                    <TableCell>
                      <Label htmlFor={`tier-from-${index}`} className="sr-only">
                        {t('products.priceTiers.columns.validFrom')}
                      </Label>
                      <Input
                        id={`tier-from-${index}`}
                        type="date"
                        disabled={!canWrite}
                        {...form.register(`priceTiers.${index}.validFrom`, {
                          setValueAs: textOrNull,
                        })}
                      />
                    </TableCell>
                    <TableCell>
                      <Label htmlFor={`tier-to-${index}`} className="sr-only">
                        {t('products.priceTiers.columns.validTo')}
                      </Label>
                      <Input
                        id={`tier-to-${index}`}
                        type="date"
                        disabled={!canWrite}
                        aria-invalid={rowError?.validTo !== undefined}
                        {...form.register(`priceTiers.${index}.validTo`, {
                          setValueAs: textOrNull,
                        })}
                      />
                      {rowError?.validTo === undefined ? null : (
                        <p role="alert" className="mt-1 text-xs text-destructive">
                          {rowError.validTo.message}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={!canWrite}
                        aria-label={t('products.priceTiers.remove')}
                        onClick={() => fieldArray.remove(index)}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {canWrite ? (
        <div className="flex flex-wrap items-center gap-2">
          {fieldArray.fields.length === 0 ? null : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                fieldArray.append({
                  priceGroupId: null,
                  priceGroup: null,
                  currency: product.currency,
                  unitCode: product.baseUnit,
                  minQuantity: '0',
                  unitPrice: product.listPrice ?? '',
                  discountPct: '0',
                  validFrom: null,
                  validTo: null,
                })
              }
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('products.priceTiers.add')}
            </Button>
          )}
          <Button type="submit" size="sm" disabled={updateProduct.isPending}>
            {updateProduct.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            {t('common.save')}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
